import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useFirebase } from 'components/Firebase/index.js';
import useOrgId from './useOrgId.js';
import { COMMON_GROUNDINGS, ONE_TO_MANY_GROUNDINGS, HOST_LABEL } from 'constants/groundings.js';
import { ONE_TO_MANY_PRODUCT } from 'constants/products.js';


const useAIGroundings = () => {
  const orgId = useOrgId();
  const { selectedProduct } = useParams();
  const firebase = useFirebase();
  const queryClient = useQueryClient();
  const commonAIGroundings = new Map();

  let isLoading = false;
  Object.values(COMMON_GROUNDINGS).forEach(groundingId => {
    const commonGrounding = useQuery({
      queryKey: ['groundings', orgId, groundingId],
      queryFn: async () => {
        const agent = await firebase.getAgent();
        const response = await agent.get(`/o/${encodeURIComponent(orgId)}/groundings/${encodeURIComponent(groundingId)}`);
        return response?.body?.results;
      },
    });
    isLoading = isLoading || commonGrounding.isLoading;
    commonAIGroundings.set(groundingId, commonGrounding);
  });

  const addGrounding = useMutation({
    mutationFn: async data => {
      const { id, ...grounding } = data;
      const agent = await firebase.getAgent();
      const response = await agent.post(`/o/${encodeURIComponent(orgId)}/groundings`, grounding);
      const { id: groundingId } = response?.body?.results || {};
      if (response?.status === 201) {
        // Grounding was added
        queryClient.setQueryData(['groundings', orgId, groundingId], response?.body?.results);
        if (selectedProduct === ONE_TO_MANY_PRODUCT && id) {
          queryClient.setQueryData(['groundings', orgId, id], (cachedResponse = []) => {
            cachedResponse.push(response?.body?.results);
            return cachedResponse;
          });
        }
      }
      return groundingId;
    },
  });

  const updateGrounding = useMutation({
    mutationFn: async grounding => {
      const agent = await firebase.getAgent();
      const { groundingId: id, id: groundingId, ...groundingData } = grounding;
      const response = await agent.put(`/o/${encodeURIComponent(orgId)}/groundings/${encodeURIComponent(groundingId)}`, {
        ...groundingData,
      });
      if (response?.status === 204) {
        // Grounding was updated
        queryClient.setQueryData(['groundings', orgId, groundingId], grounding);
        if (selectedProduct === ONE_TO_MANY_PRODUCT && id) {
          queryClient.setQueryData(['groundings', orgId, id], (cachedResponse = []) => {
            return cachedResponse.map(cachedGrounding => {
              if (cachedGrounding.id !== groundingId) return cachedGrounding;
              return {
                id: groundingId,
                ...groundingData,
              };
            });
          });
        }
      } else if (response?.status === 201) {
        // Grounding was created
        queryClient.setQueryData(['groundings', orgId, groundingId], response?.body?.results);
      }
      return groundingId;
    },
  });

  const removeGrounding = useMutation({
    mutationFn: async ({ groundingId, id }) => {
      const agent = await firebase.getAgent();
      const response = await agent.delete(`/o/${encodeURIComponent(orgId)}/groundings/${encodeURIComponent(groundingId)}`);
      if (response?.status === 204) {
        queryClient.removeQueries(['groundings', orgId, groundingId]);
        if (selectedProduct === ONE_TO_MANY_PRODUCT && id) {
          queryClient.setQueryData(['groundings', orgId, id], (cachedResponse = []) => {
            return cachedResponse.filter(grounding => grounding.id !== groundingId);
          });
        }
      }
      return groundingId;
    },
  });

  const hooks = {
    commonAIGroundings,
    addGrounding,
    updateGrounding,
    removeGrounding,
  };

  if (selectedProduct === ONE_TO_MANY_PRODUCT) {
    const oneToManyAIGroundings = new Map();
    Object.values(ONE_TO_MANY_GROUNDINGS).forEach(groundingId => {
      oneToManyAIGroundings.set(groundingId, useQuery({
        queryKey: ['groundings', orgId, groundingId],
        queryFn: async () => {
          const agent = await firebase.getAgent();
          const response = await agent.get(`/o/${encodeURIComponent(orgId)}/groundings?labels=${HOST_LABEL}`);
          return response?.body?.results;
        },
      }));
    });
    hooks.oneToManyAIGroundings = oneToManyAIGroundings;
  }

  return {
    isLoading: isLoading || addGrounding.isLoading || removeGrounding.isLoading || updateGrounding.isLoading,
    ...hooks,
  };
};

export default useAIGroundings;
